import apiClient from "./client";

import type { components, operations } from "@/types/api.generated";

export type StageState = components["schemas"]["ExtractionStage"]["state"];
export type ConfigNode = components["schemas"]["BulkVerdictRequest"]["node"];
export type ActivityNode =
  components["schemas"]["app__routers__config_server_stages__ActivityListResponse"]["node"];
export type NodeCounts = components["schemas"]["NodeCountsOut"];
export type ConfigServerOverview =
  components["schemas"]["ConfigServerOverview"];
export type ExtractionSummary = components["schemas"]["ExtractionSummary"];
export type ExtractionSummaryQuery = NonNullable<
  operations["get_extraction_summary_api_projects__project_id__configuration_server_extraction_summary_get"]["parameters"]["query"]
>;
export type ConfigActivityRow = components["schemas"]["ActivityListRow"];
export type ActivityTablesResponse =
  components["schemas"]["ActivityTablesResponse"];
export type ConfigActivitiesResponse =
  components["schemas"]["app__routers__config_server_stages__ActivityListResponse"];
export type VerdictItem = components["schemas"]["BulkVerdictItem"];
export type BulkVerdictResult = components["schemas"]["BulkVerdictResponse"];

/** Os erros de domínio chegam em HTTPException.detail, fora dos schemas de sucesso. */
export interface BulkVerdictError {
  code:
    | "item_already_executed"
    | "item_conversation_started"
    | "item_not_in_universe"
    | "no_extraction"
    | "unit_from_diagnostic"
    | "parent_from_diagnostic"
    | "child_from_diagnostic";
  message: string;
  items: VerdictItem[];
}

const base = (projectId: string) =>
  `/api/projects/${projectId}/configuration-server`;

export async function getConfigServerOverview(
  projectId: string,
): Promise<ConfigServerOverview> {
  const response = await apiClient.get<ConfigServerOverview>(
    `${base(projectId)}/overview`,
  );
  return response.data;
}

export async function getExtractionSummary(
  projectId: string,
  query?: ExtractionSummaryQuery,
): Promise<ExtractionSummary> {
  const path = `${base(projectId)}/extraction-summary`;
  const response = query
    ? await apiClient.get<ExtractionSummary>(path, { params: query })
    : await apiClient.get<ExtractionSummary>(path);
  return response.data;
}

export interface ExecutionActivitiesQuery {
  area?: string;
  q?: string;
  cursor?: string | null;
  limit?: number;
}
export type ExecutionActivityRow = ConfigActivityRow;
export type ExecutionActivityPage = ConfigActivitiesResponse;
export type ExecutionAreasResponse =
  components["schemas"]["ExecutionActivityAreasResponse"];
export type RunnableCodesResponse =
  components["schemas"]["RunnableActivityResponse"];
export type ActivityStatusResponse =
  components["schemas"]["ActivityStatusResponse"];
export type ExtractionArtifactResponse =
  components["schemas"]["ExtractionArtifact"];
export async function getExtractionArtifact(
  projectId: string,
): Promise<ExtractionArtifactResponse> {
  const response = await apiClient.get<ExtractionArtifactResponse>(
    `${base(projectId)}/extraction-artifact`,
  );
  return response.data;
}
export async function listConfigActivities(
  projectId: string,
  node: ActivityNode,
  query: ExecutionActivitiesQuery = {},
): Promise<ExecutionActivityPage> {
  const response = await apiClient.get<ExecutionActivityPage>(
    `${base(projectId)}/config-activities`,
    { params: { node, ...query } },
  );
  return response.data;
}
export async function listExecutionActivityAreas(
  projectId: string,
  node: ActivityNode,
): Promise<ExecutionAreasResponse> {
  const response = await apiClient.get<ExecutionAreasResponse>(
    `${base(projectId)}/config-activities/areas`,
    { params: { node } },
  );
  return response.data;
}
export async function listRunnableActivityCodes(
  projectId: string,
  node: ActivityNode,
): Promise<RunnableCodesResponse> {
  const response = await apiClient.get<RunnableCodesResponse>(
    `${base(projectId)}/config-activities/runnable-codes`,
    { params: { node } },
  );
  return response.data;
}
export async function getConfigActivityStatuses(
  projectId: string,
  node: ActivityNode,
  codes: string[],
): Promise<ActivityStatusResponse> {
  const response = await apiClient.post<ActivityStatusResponse>(
    `${base(projectId)}/config-activities/status`,
    { codes },
    { params: { node } },
  );
  return response.data;
}

export async function listConfigActivityTables(
  projectId: string,
  node: ActivityNode,
  activityCode: string,
  artifactId?: string,
): Promise<ActivityTablesResponse> {
  const response = await apiClient.get<ActivityTablesResponse>(
    `${base(projectId)}/config-activities/${encodeURIComponent(activityCode)}/tables`,
    { params: { node, ...(artifactId ? { artifact_id: artifactId } : {}) } },
  );
  return response.data;
}

/** Várias decisões num nó, numa chamada. Na estrutura organizacional a cascata é do servidor. */
export async function setScopeVerdicts(
  projectId: string,
  node: ConfigNode,
  verdict: components["schemas"]["BulkVerdictRequest"]["verdict"],
  items: VerdictItem[],
): Promise<BulkVerdictResult> {
  const body: components["schemas"]["BulkVerdictRequest"] = {
    node,
    verdict,
    items,
  };
  const response = await apiClient.post<BulkVerdictResult>(
    `${base(projectId)}/scope-verdicts`,
    body,
  );
  return response.data;
}

/** Rascunho do Gerenciamento: as decisões na ordem feita, numa transação. */
export async function saveScopeVerdicts(
  projectId: string,
  operations: components["schemas"]["DraftRequest"]["operations"],
): Promise<components["schemas"]["SaveResponse"]> {
  const body: components["schemas"]["DraftRequest"] = { operations };
  const response = await apiClient.post<components["schemas"]["SaveResponse"]>(
    `${base(projectId)}/scope-verdicts/batch`,
    body,
  );
  return response.data;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isVerdictItem(value: unknown): value is VerdictItem {
  return (
    isRecord(value) &&
    typeof value.item_type === "string" &&
    typeof value.item_key === "string"
  );
}

function isBulkVerdictDetail(value: unknown): value is BulkVerdictError {
  return (
    isRecord(value) &&
    (value.code === "item_already_executed" ||
      value.code === "item_conversation_started" ||
      value.code === "item_not_in_universe" ||
      value.code === "no_extraction" ||
      value.code === "unit_from_diagnostic" ||
      value.code === "parent_from_diagnostic" ||
      value.code === "child_from_diagnostic") &&
    typeof value.message === "string" &&
    Array.isArray(value.items) &&
    value.items.every(isVerdictItem)
  );
}

/** O detalhe estruturado de 404/409/422 da rota em lote, ou null se for outro erro. */
export function bulkVerdictError(error: unknown): BulkVerdictError | null {
  if (
    !isRecord(error) ||
    !isRecord(error.response) ||
    !isRecord(error.response.data)
  ) {
    return null;
  }
  const detail = error.response.data.detail;
  return isBulkVerdictDetail(detail) ? detail : null;
}

export type ExtractionProgress = components["schemas"]["ExtractionProgressOut"];

export interface ExtractionRefreshStatus {
  available: boolean;
  artifact_id: string | null;
  extracted_at?: string | null;
  mapping_changed_at?: string | null;
  /** Andamento da leitura paginada; só vem com o refresh em running (spec 2026-10-29). */
  progress?: ExtractionProgress | null;
  refresh: {
    status: "running" | "done" | "failed";
    tables_reread?: number;
    fields_added?: number;
    records_read?: number;
    message?: string | null;
    errors?: string[];
    warnings?: string[];
  } | null;
}

export async function getExtractionRefresh(
  projectId: string,
): Promise<ExtractionRefreshStatus> {
  const res = await apiClient.get<ExtractionRefreshStatus>(
    `/api/projects/${encodeURIComponent(projectId)}/configuration-server/extraction-refresh`,
  );
  return res.data;
}

export async function startExtractionRefresh(
  projectId: string,
): Promise<ExtractionRefreshStatus> {
  const res = await apiClient.post<ExtractionRefreshStatus>(
    `/api/projects/${encodeURIComponent(projectId)}/configuration-server/extraction-refresh`,
  );
  return res.data;
}
